import React, { useState, useEffect } from 'react';
import pieceService from '../../../services/pieceService';
import { useTheme } from '../../../contexts/ThemeContext';

const DeletePieceModal = ({ isOpen, onClose, onSuccess, piece }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const handleDelete = async () => {
    if (confirmText !== (piece?.designation || '')) {
      setError('Le texte de confirmation ne correspond pas');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await pieceService.deletePiece(piece.id);
      if (result.success) {
        onSuccess();
        onClose();
      } else {
        setError(result.message || "Erreur lors de la suppression");
      }
    } catch {
      setError("Erreur lors de la suppression");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setConfirmText('');
    setError('');
    setLoading(false);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setConfirmText('');
      setError('');
      setLoading(false);
    }
  }, [isOpen]);

  if (!isOpen || !piece) return null;
  const confirmationText = piece.designation || '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className={`rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`px-6 py-4 flex items-center justify-between ${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-red-600 to-red-700'}`}>
          <div className="flex items-center">
            <div className="w-10 h-10 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-3">
              <i className="fas fa-trash text-white text-lg"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Supprimer Pièce / Service</h2>
              <p className={`${isDarkMode ? 'text-red-200' : 'text-red-100'} text-sm`}>{piece?.designation}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>
        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {error && (
            <div className={`mb-4 p-3 rounded border ${isDarkMode ? 'bg-gray-900 border-gray-800 text-gray-200' : 'bg-red-50 border-red-200 text-red-700'}`}>
              {error}
            </div>
          )}
          <div className={`${isDarkMode ? 'bg-gray-900 border border-gray-800' : 'bg-red-50 border border-red-200'} rounded-xl p-6 mb-6`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-red-100'} w-10 h-10 rounded-lg flex items-center justify-center`}>
                  <i className={`fas fa-exclamation-triangle ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}></i>
                </div>
              </div>
              <div className="ml-4">
                <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-red-200' : 'text-red-900'}`}>
                  Attention : Suppression définitive
                </h3>
                <div className={`${isDarkMode ? 'text-red-300' : 'text-red-700'} space-y-2`}>
                  <p>Vous êtes sur le point de supprimer définitivement cette pièce/service :</p>
                  <div className={`${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-red-200'} rounded-lg p-4`}>
                    <div className="flex items-center">
                      <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-red-50'} w-10 h-10 rounded-lg flex items-center justify-center mr-4`}>
                        <i className={`fas fa-cogs ${isDarkMode ? 'text-red-400' : 'text-red-600'} text-lg`}></i>
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                          {piece.designation || 'Sans nom'}
                        </h4>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          ID: {piece.id}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Confirmation Input */}
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-xl p-6 mb-6`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
              Confirmation de suppression
            </h3>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-4`}>
              Pour confirmer la suppression, veuillez taper exactement le texte suivant :
            </p>
            <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'} rounded-lg p-3 mb-4`}>
              <code className={`text-lg font-mono font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                {confirmationText}
              </code>
            </div>
            <div className="space-y-2">
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Tapez le texte pour confirmer
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors font-mono
                  ${isDarkMode ? 'bg-gray-900 border-gray-700 text-gray-100 placeholder-gray-400' : 'border-gray-300'}`}
                placeholder={confirmationText}
              />
            </div>
            {confirmText && confirmText !== confirmationText && (
              <p className="text-red-600 text-sm mt-2">
                <i className="fas fa-times-circle mr-1"></i>
                Le texte ne correspond pas
              </p>
            )}
            {confirmText === confirmationText && (
              <p className="text-green-600 text-sm mt-2">
                <i className="fas fa-check-circle mr-1"></i>
                Confirmation valide
              </p>
            )}
          </div>
          {/* Action Buttons */}
          <div className={`flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              type="button"
              onClick={handleClose}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                isDarkMode
                  ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className="fas fa-times mr-2"></i>
              Annuler
            </button>
            <button
              onClick={handleDelete}
              disabled={loading || confirmText !== confirmationText}
              className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${
                loading || confirmText !== confirmationText
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : isDarkMode
                  ? 'bg-red-700 hover:bg-red-800 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Suppression...
                </>
              ) : (
                <>
                  <i className="fas fa-trash mr-2"></i>
                  Supprimer définitivement
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeletePieceModal;